import { memo, useMemo } from "react";
import {
  diffClass,
  formatDiff,
  holeInCourseOf,
  sum,
  type ScoreSection,
} from "../../../lib/utils/score.utils";
import type { ScoreCard } from "../../../@apis/dto/response/score.response";
import { cn } from "../../../lib/utils/cn";

export function ScoreMark(score: number, par?: number) {
  // 미입력/0점/NaN/파 미지정이면 마킹 없이 숫자만
  if (!Number.isFinite(score) || score <= 0 || !Number.isFinite(par)) {
    return (
      <span className="font-bold">
        {Number.isFinite(score) && score > 0 ? score : ""}
      </span>
    );
  }

  const diff = score - (par as number);

  // em 기준 컨테이너: 폰트 크기(=부모 셀의 font-size)에 비례해서 커짐/작아짐
  const wrapStyle: React.CSSProperties = { width: "1.4em", height: "1.4em" };
  const outerLine = { width: "1.4em", height: "1.4em" };
  const innerLine = { width: "1.0em", height: "1.0em" };
  const singleLine = { width: "1.4em", height: "1.4em" };

  const number = (
    <span className="absolute inset-0 flex items-center justify-center font-bold leading-none">
      {score}
    </span>
  );

  if (score === 1) {
    // 홀인원
    return (
      <span
        className="relative flex items-center justify-center"
        style={wrapStyle}
      >
        <img
          src="/icons/ic-star.webp"
          alt="홀인원"
          style={{
            width: "1.3em",
            height: "1.3em",
            objectFit: "contain",
            aspectRatio: "1 / 1",
          }}
        />
      </span>
    );
  }

  if (diff <= -2) {
    // 이글 이하
    return (
      <span
        className="relative flex items-center justify-center"
        style={wrapStyle}
      >
        <div
          className="rounded-full border-2 border-[#e5002b] absolute"
          style={outerLine}
        />
        <div
          className="rounded-full border-2 border-[#e5002b] absolute"
          style={innerLine}
        />
        {number}
      </span>
    );
  }

  if (diff === -1) {
    // 버디
    return (
      <span
        className="relative flex items-center justify-center"
        style={wrapStyle}
      >
        <div
          className="rounded-full border-2 border-[#e5002b]"
          style={singleLine}
        />
        {number}
      </span>
    );
  }

  if (diff === 1) {
    // 보기
    return (
      <span
        className="relative flex items-center justify-center"
        style={wrapStyle}
      >
        <div
          className="bg-white border-2 border-[#155be9]"
          style={singleLine}
        />
        {number}
      </span>
    );
  }

  if (diff >= 2) {
    // 더블보기 이상
    return (
      <span
        className="relative flex items-center justify-center"
        style={wrapStyle}
      >
        <div className="border-2 border-[#155be9] absolute" style={outerLine} />
        <div className="border-2 border-[#155be9] absolute" style={innerLine} />
        {number}
      </span>
    );
  }

  // 파
  return <span className="font-bold">{score}</span>;
}

/** ========= NineRowTable (홀/파/스코어 한 줄) ========= */
export type NineRowTableProps = {
  labelLeft: "홀" | "파" | "스코어";
  /** 라운드 내 슬롯 번호 */
  holeNos: number[];
  cells: (number | null)[];
  currentHoleNo?: number;
  pars?: (number | null)[];
  onClick?: (holeNo: number) => void;
};

export const NineRowTable = memo(function NineRowTable({
  labelLeft,
  holeNos,
  cells,
  currentHoleNo,
  pars,
  onClick,
}: NineRowTableProps) {
  const activeIdx = useMemo(
    () =>
      typeof currentHoleNo === "number" ? holeNos.indexOf(currentHoleNo) : -1,
    [holeNos, currentHoleNo],
  );

  return (
    <div
      role="row"
      className="grid w-full min-w-max border-b border-[#e6e6e6] text-[0.875rem]"
      style={{
        // 행마다 별도 grid라 라벨 열은 auto 대신 고정폭이어야 홀 열이 행끼리 맞는다
        // em 기준이라 시스템 글자 크기를 키워도 "스코어"·점수 마크가 칸을 넘지 않는다
        gridTemplateColumns: `max(4.5rem, 4em) repeat(${holeNos.length}, minmax(max(2rem, 2.2em), 1fr))`,
      }}
    >
      <div
        role="rowheader"
        className={`flex items-center whitespace-nowrap px-[0.4rem] py-[0.55rem] text-left font-[500] text-[0.875rem] ${
          labelLeft === "홀" ? "text-[#888]" : "text-[#222]"
        }`}
      >
        {labelLeft}
      </div>
      {holeNos.map((holeNo, i) => {
        const isActive = i === activeIdx;
        const baseColor = labelLeft === "홀" ? "text-[#888]" : "text-[#222]";
        const value = cells[i];
        const parForMark =
          labelLeft === "스코어" && !isActive
            ? (pars?.[i] ?? undefined)
            : undefined;

        return (
          <div
            key={holeNo}
            role="cell"
            className={cn(
              "flex items-center justify-center px-[0.1rem] py-[0.35rem] text-center font-[500] text-[1.25rem]",
              isActive
                ? "bg-[#e5002b] text-white border-b border-white"
                : baseColor,
            )}
            onClick={() => onClick?.(holeNo)}
          >
            {labelLeft === "스코어" ? (
              <span className="relative flex items-center justify-center font-bold">
                {ScoreMark(Number(value), parForMark)}
              </span>
            ) : (
              value
            )}
          </div>
        );
      })}
    </div>
  );
});

/** ========= 코스 합계 행 ========= */
export type CourseRowProps = {
  label: string;
  total: number;
  diff: number | null;
};

export const CourseRow = memo(function CourseRow({
  label,
  total,
  diff,
}: CourseRowProps) {
  return (
    <div className="flex min-h-[2.5rem] items-center justify-between gap-2 border-b border-[#e6e6e6] bg-[#f5f5f5] p-2 font-[500] text-[#222] last:border-b-0">
      <span className="min-w-0 break-keep font-[500] text-[1.25rem] leading-snug">
        {label}
      </span>
      <span className="shrink-0 font-bold text-[1.5rem] leading-none">
        {total}
        {diff != null && (
          <span
            className={`ml-[4px] font-[500] text-[1.125rem] ${diffClass(diff)}`}
          >
            {formatDiff(diff)}
          </span>
        )}
      </span>
    </div>
  );
});

/** ========= 카드 이름/점수 라인 ========= */
export function ScoreCardHeader({
  card,
  className,
}: {
  card: ScoreCard;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-x-[8px] text-[#222] font-bold text-xl min-w-0",
        className,
      )}
    >
      <span className="min-w-0 break-keep [overflow-wrap:anywhere]">
        {card.userName}
      </span>
      <span className="shrink-0">
        {card.totalScore}
        {card.isComplete && (
          <span
            className={`ml-[3px] font-[500] text-sm ${diffClass(card.overUnderScore || 0)}`}
          >
            {formatDiff(card.overUnderScore || 0)}
          </span>
        )}
      </span>
    </div>
  );
}

/** ========= 범례  ========= */
export const Legend = memo(function Legend() {
  return (
    <div className="mt-[10px] flex items-center gap-[10px] text-[#888] font-[500] flex-wrap">
      <span className="flex items-center gap-[5px] text-xs">
        <img className="w-3" src="/icons/ic-star.webp" alt="홀인원" />
        홀인원
      </span>
      <span className="flex items-center gap-[5px] text-xs">
        <div className="size-3 rounded-full border border-[#e5002b] flex items-center justify-center">
          <div className="size-2 rounded-full border border-[#e5002b]" />
        </div>
        이글 이하
      </span>
      <span className="flex items-center gap-[5px] text-xs">
        <div className="size-3 rounded-full border border-[#e5002b]" />
        버디
      </span>
      <span className="flex items-center gap-[5px] text-xs">
        <div className="size-3  bg-white border border-[#155be9]" />
        보기
      </span>
      <span className="flex items-center gap-[5px] text-xs">
        <div className="size-3  border border-[#155be9] flex items-center justify-center">
          <div className="size-2 border border-[#155be9]" />
        </div>
        더블보기 이상
      </span>
    </div>
  );
});

/** ========= 코스별 9홀 테이블 + 합계 ========= */
export type ScoreCellType = "hole" | "par" | "score";

export type ScoreTablesProps = {
  sections: ScoreSection[];
  currentHoleNo?: number;
  onClickCell?: (holeNo: number, rowType: ScoreCellType) => void;
};

// 모든 홀을 입력했을 때만 오버/언더를 낸다
const summarize = ({ pars, scores }: Pick<ScoreSection, "pars" | "scores">) => {
  const total = sum(scores.map((s) => s ?? 0));
  const isComplete = scores.length > 0 && scores.every((s) => (s ?? 0) > 0);
  return {
    total,
    diff: isComplete ? total - sum(pars.map((p) => p ?? 0)) : null,
  };
};

export const ScoreTables = memo(function ScoreTables({
  sections,
  currentHoleNo,
  onClickCell,
}: ScoreTablesProps) {
  const overall = summarize({
    pars: sections.flatMap((s) => s.pars),
    scores: sections.flatMap((s) => s.scores),
  });

  return (
    <div className="mt-[21px] overflow-x-auto overflow-y-hidden rounded-[10px] border border-[#e6e6e6] bg-white">
      <div className="min-w-full w-max">
      {sections.map((section) => {
        const { total, diff } = summarize(section);
        return (
          <div key={section.order}>
            <NineRowTable
              labelLeft="홀"
              holeNos={section.holeNos}
              cells={section.holeNos.map(holeInCourseOf)}
              currentHoleNo={currentHoleNo}
              onClick={(holeNo) => onClickCell?.(holeNo, "hole")}
            />
            <NineRowTable
              labelLeft="파"
              holeNos={section.holeNos}
              cells={section.pars}
              currentHoleNo={currentHoleNo}
              onClick={(holeNo) => onClickCell?.(holeNo, "par")}
            />
            <NineRowTable
              labelLeft="스코어"
              holeNos={section.holeNos}
              cells={section.scores}
              currentHoleNo={currentHoleNo}
              pars={section.pars}
              onClick={(holeNo) => onClickCell?.(holeNo, "score")}
            />
            <CourseRow label={section.title} total={total} diff={diff} />
          </div>
        );
      })}
      {sections.length > 1 && (
        <CourseRow label="합계" total={overall.total} diff={overall.diff} />
      )}
      </div>
    </div>
  );
});

export default ScoreTables;
